import type { WorklistAccessStatus } from "../worklist/useWorklistAccessGate";

export interface AdtBellResolvedProps {
  /**
   * MAJOR 1 (grill fix): false unless the worklist access gate is fully
   * `"ready"` AND a real PP-bearer-derived `userId` resolved — mirrors
   * WorklistPage.tsx's own hard gate (`status !== "ready"` -> no render)
   * rather than trusting `AdtNotifications`'s internal `isGated` check
   * alone. That check is an internal safety net, not a contract this host
   * should depend on staying in place across an upstream refactor — an
   * unscoped PHI query (empty `companyId`/`userId`) must never even be
   * attempted from this side.
   */
  shouldRender: boolean;
  companyId: string;
  userId: string;
  providerNpi: string;
}

/**
 * Pure prop derivation for `AdtBell.tsx` — extracted into its own module so
 * it's unit-testable WITHOUT importing `AdtBell.tsx` itself, which statically
 * imports `@astrana/ui/modules` (see `AdtBell.tsx`'s header for why that
 * import must stay out of any file this repo's default Vitest run touches:
 * that bundle's extensionless nested `lucide-react/dynamic` import fails
 * Vitest's stricter ESM resolver).
 *
 * Takes the RESOLVED identity trio (`status`/`resolvedIpaId`/`npi` from
 * `useWorklistAccessGate()`, `nativeUserId` from `resolveWorklistIdentity`)
 * rather than a raw bearer token, so a test can exercise every gating branch
 * with plain values — `resolveWorklistIdentity`'s own JWT-decode contract is
 * already covered by `resolveWorklistIdentity.test.ts`.
 */
export function resolveAdtBellProps(
  status: WorklistAccessStatus,
  resolvedIpaId: string | null,
  npi: string | null,
  nativeUserId: string | undefined,
): AdtBellResolvedProps {
  return {
    shouldRender: status === "ready" && !!nativeUserId,
    companyId: resolvedIpaId ?? "",
    userId: nativeUserId ?? "",
    providerNpi: npi ?? "",
  };
}

/**
 * Extracts a small, PHI-SAFE dedupe key from an ADT bell `onError` payload.
 * The module's error is an RTK Query `FetchBaseQueryError`-shaped object
 * carrying `status` (and, for an HTTP error, `data` — the response BODY,
 * which can carry PHI); anything else (a plain `Error`, a network failure)
 * collapses to `"unknown"`. Deliberately reads ONLY `status`, never `data` —
 * see `AdtBell.tsx`'s `handleError` for the console-logging use that relies
 * on the same restriction.
 */
export function adtBellErrorKey(e: unknown): string {
  if (e && typeof e === "object" && "status" in e) {
    return String((e as { status: unknown }).status);
  }
  return "unknown";
}

/**
 * True for a 401/403 — an EXPECTED outcome once the backend entitlement gate
 * denies this provider/IPA (e.g. a "view as" peek), not a failure worth
 * alerting on every 30s poll. Mirrors `IpaForbiddenState`'s no-retry stance
 * for the worklist's own equivalent gate (MAJOR 5, grill fix).
 */
export function isAdtBellAuthFailure(e: unknown): boolean {
  const key = adtBellErrorKey(e);
  return key === "401" || key === "403";
}
